import { useState, useEffect, useMemo } from 'react';
import {
  TrendingUp, TrendingDown, AlertTriangle, RefreshCw, Loader2, Shield,
  ChevronDown, X, DollarSign, Activity, Zap, Lock, BarChart2, Target,
  ArrowUpRight, ArrowDownRight, Info, Check, Gauge, Calculator, Share2,
  Sparkles, StopCircle, Grid3X3, Clock, Percent, XOctagon, ArrowRight,
  Crosshair, Eye, Brain, MessageSquare, Copy, ChevronRight, Timer
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { InfoTip } from '../components/ui';
import LeverageTierBadge, { classifyLeverage } from '../components/LeverageTierBadge';
import {
  getExchangeConnections, getFuturesPositions,
  getFuturesBalance, openFuturesPosition,
  closeFuturesPosition, getExchangeTicker,
} from '../services/api';

// ─── Constants ──────────────────────────────────────────────────────────────

// Pair preset list — symbols only. Live prices are fetched per-pair from the
// connected exchange via getExchangeTicker (no hardcoded prices that go stale).
// TODO post-launch: replace with dynamic fetchFuturesMarkets per exchange so
// the user sees all available pairs, not just this curated top-12.
const POPULAR_PAIRS = [
  { symbol: 'BTC/USDT:USDT',   label: 'BTC' },
  { symbol: 'ETH/USDT:USDT',   label: 'ETH' },
  { symbol: 'SOL/USDT:USDT',   label: 'SOL' },
  { symbol: 'BNB/USDT:USDT',   label: 'BNB' },
  { symbol: 'XRP/USDT:USDT',   label: 'XRP' },
  { symbol: 'DOGE/USDT:USDT',  label: 'DOGE' },
  { symbol: 'ADA/USDT:USDT',   label: 'ADA' },
  { symbol: 'AVAX/USDT:USDT',  label: 'AVAX' },
  { symbol: 'LINK/USDT:USDT',  label: 'LINK' },
  { symbol: 'DOT/USDT:USDT',   label: 'DOT' },
  { symbol: 'MATIC/USDT:USDT', label: 'MATIC' },
  { symbol: 'ARB/USDT:USDT',   label: 'ARB' },
];

const LEVERAGE_PRESETS = [1, 2, 3, 5, 10, 20, 25, 50, 75, 100, 125];
const PARTIAL_CLOSE_PRESETS = [25, 50, 75, 100];

// ─── Utility Functions ─────────────────────────────────────────────────────

function calcLiquidationPrice(entryPrice, leverage, side, marginMode) {
  if (!entryPrice || !leverage) return null;
  const maintenanceRate = marginMode === 'cross' ? 0.004 : 0.005;
  if (side === 'buy') return entryPrice * (1 - 1 / leverage + maintenanceRate);
  return entryPrice * (1 + 1 / leverage - maintenanceRate);
}

function calcRiskReward(entry, stopLoss, takeProfit, side) {
  if (!entry || !stopLoss || !takeProfit) return null;
  const risk = side === 'buy' ? entry - stopLoss : stopLoss - entry;
  const reward = side === 'buy' ? takeProfit - entry : entry - takeProfit;
  if (risk <= 0 || reward <= 0) return null;
  return { risk, reward, ratio: (reward / risk).toFixed(2), riskPct: ((risk / entry) * 100).toFixed(2), rewardPct: ((reward / entry) * 100).toFixed(2) };
}

function calcPositionSize(balance, riskPct, entryPrice, stopLoss, leverage, side) {
  if (!balance || !riskPct || !entryPrice || !stopLoss) return null;
  const riskAmount = balance * (riskPct / 100);
  const priceDiff = side === 'buy' ? entryPrice - stopLoss : stopLoss - entryPrice;
  if (priceDiff <= 0) return null;
  const qty = (riskAmount / priceDiff);
  return { qty: qty.toFixed(6), positionValue: (qty * entryPrice).toFixed(2), marginNeeded: (qty * entryPrice / leverage).toFixed(2) };
}

function formatCountdown(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

// Pre-trade risk check — rule-based, NOT AI. Computes a deterministic risk
// score from the leverage value the user has selected and produces honest
// liquidation-distance warnings. We don't claim "market regime detection"
// because nothing in this function looks at real market data — the regime
// label was a hardcoded function of leverage, which was misleading.
function getPreTradeRiskCheck(symbol, side, leverage) {
  const pair = POPULAR_PAIRS.find(p => p.symbol === symbol);
  const name = pair?.label || symbol;
  const riskScore = leverage > 50 ? 9 : leverage > 20 ? 7 : leverage > 10 ? 5 : 3;

  const warnings = [];
  if (leverage > 50) warnings.push('Extreme leverage — at 50x+, even small moves against you trigger liquidation. Most retail accounts at this leverage are wiped within 24h.');
  if (leverage > 20) warnings.push(`At ${leverage}x, a ${(100 / leverage).toFixed(1)}% move against your position = 100% loss.`);

  const suggestions = [];
  if (leverage > 20) suggestions.push(`Reducing to ${Math.min(leverage, 20)}x increases your liquidation buffer significantly.`);
  if (!warnings.length) suggestions.push('Leverage is in a reasonable range. Set both TP and SL before opening — every position needs both an exit-on-win plan and an exit-on-loss plan.');

  return { riskScore, warnings, suggestions, name };
}

// ─── Sub-Components ────────────────────────────────────────────────────────

// FundingRateCard removed — was rendering hardcoded values for 3 symbols only.
// Funding rates are exchange-specific and must be fetched via the actual
// exchange's API (ccxt fetchFundingRate). To re-add post-launch:
//   1. Add a backend route /api/futures/funding-rate/{exchangeId}/{symbol}
//   2. Wire it through ccxt's fetchFundingRate
//   3. Cache 60s; refresh on symbol change
// Until that lands, no funding card. Better than a fake one.

function LiquidationCalculator({ entryPrice, leverage, side, marginMode }) {
  const liqPrice = calcLiquidationPrice(entryPrice, leverage, side, marginMode);
  if (!liqPrice || !entryPrice) return null;
  const distance = Math.abs(entryPrice - liqPrice) / entryPrice * 100;
  return (
    <div className="flex justify-between text-xs">
      <span className="text-gray-500 flex items-center gap-1"><Calculator className="w-3 h-3" /> Est. Liquidation</span>
      <span className={`font-mono font-bold ${distance < 5 ? 'text-red-400' : distance < 15 ? 'text-amber-400' : 'text-gray-300'}`}>
        ${liqPrice.toLocaleString(undefined, { maximumFractionDigits: 2 })}
        <span className="text-gray-600 font-normal ml-1">({distance.toFixed(1)}% away)</span>
      </span>
    </div>
  );
}

function RiskRewardBar({ entry, stopLoss, takeProfit, side }) {
  const rr = calcRiskReward(entry, stopLoss, takeProfit, side);
  if (!rr) return null;
  const total = rr.risk + rr.reward;
  const riskWidth = (rr.risk / total) * 100;
  const rewardWidth = (rr.reward / total) * 100;
  const isGood = parseFloat(rr.ratio) >= 2;
  return (
    <div className="bg-dark-700/50 rounded-xl p-3 border border-dark-600/30">
      <div className="flex items-center justify-between mb-2">
        <span className="text-gray-500 text-[10px] flex items-center gap-1"><Crosshair className="w-3 h-3" /> Risk / Reward</span>
        <span className={`text-xs font-bold ${isGood ? 'text-emerald-400' : 'text-amber-400'}`}>
          1:{rr.ratio} R:R {isGood && '✓'}
        </span>
      </div>
      <div className="flex h-3 rounded-full overflow-hidden">
        <div className="bg-red-500/60" style={{ width: `${riskWidth}%` }} />
        <div className="bg-emerald-500/60" style={{ width: `${rewardWidth}%` }} />
      </div>
      <div className="flex justify-between mt-1.5 text-[10px]">
        <span className="text-red-400">Risk: ${rr.risk.toFixed(2)} ({rr.riskPct}%)</span>
        <span className="text-emerald-400">Reward: ${rr.reward.toFixed(2)} ({rr.rewardPct}%)</span>
      </div>
    </div>
  );
}

// Pre-trade risk check card. Rule-based, deterministic — NOT AI. Renamed from
// "AI Pre-Trade" to be honest about what it actually does: warn about high
// leverage based on the value the user picked. No market analysis happening.
function PreTradeRiskCard({ symbol, side, leverage }) {
  const analysis = useMemo(() => getPreTradeRiskCheck(symbol, side, leverage), [symbol, side, leverage]);
  const scoreColor = analysis.riskScore > 7 ? 'text-red-400' : analysis.riskScore > 4 ? 'text-amber-400' : 'text-emerald-400';
  const scoreBg = analysis.riskScore > 7 ? 'from-red-500/10 to-red-500/5' : analysis.riskScore > 4 ? 'from-amber-500/10 to-amber-500/5' : 'from-emerald-500/10 to-emerald-500/5';

  return (
    <div className={`rounded-xl p-3 border border-dark-600/30 bg-gradient-to-br ${scoreBg}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold text-white flex items-center gap-1.5">
          <Shield className="w-3.5 h-3.5 text-amber-400" /> Pre-Trade Risk Check
        </span>
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-gray-500">Score:</span>
          <span className={`text-sm font-bold ${scoreColor}`}>{analysis.riskScore}/10</span>
        </div>
      </div>
      {analysis.warnings.map((w, i) => (
        <div key={i} className="flex items-start gap-1.5 mb-1.5">
          <AlertTriangle className="w-3 h-3 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-[10px] text-red-300">{w}</p>
        </div>
      ))}
      {analysis.suggestions.map((s, i) => (
        <div key={i} className="flex items-start gap-1.5 mb-1">
          <Info className="w-3 h-3 text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-[10px] text-gray-400">{s}</p>
        </div>
      ))}
    </div>
  );
}

function PositionSizeCalculator({ entryPrice, stopLoss, leverage, side, marginBalance }) {
  const [riskPct, setRiskPct] = useState(2);
  const result = calcPositionSize(marginBalance, riskPct, entryPrice, stopLoss, leverage, side);

  return (
    <div className="bg-dark-700/50 rounded-xl p-3 border border-dark-600/30">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] text-gray-500 flex items-center gap-1"><Calculator className="w-3 h-3" /> Position Size Calculator</span>
      </div>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] text-gray-500">Risk per trade:</span>
        {[1, 2, 3, 5].map(p => (
          <button key={p} onClick={() => setRiskPct(p)}
            className={`px-2 py-0.5 rounded text-[9px] font-bold ${riskPct === p ? 'bg-amber-400 text-dark-900' : 'bg-dark-600 text-gray-500'}`}>{p}%</button>
        ))}
      </div>
      {result ? (
        <div className="space-y-1">
          <div className="flex justify-between text-[10px]"><span className="text-gray-500">Suggested qty</span><span className="text-white font-mono">{result.qty}</span></div>
          <div className="flex justify-between text-[10px]"><span className="text-gray-500">Position value</span><span className="text-white font-mono">${result.positionValue}</span></div>
          <div className="flex justify-between text-[10px]"><span className="text-gray-500">Margin needed</span><span className="text-amber-400 font-mono">${result.marginNeeded}</span></div>
        </div>
      ) : (
        <p className="text-[10px] text-gray-600">Set entry price and stop loss to calculate</p>
      )}
    </div>
  );
}

function PortfolioHeatMap({ positions }) {
  if (positions.length === 0) return null;
  const maxNotional = Math.max(...positions.map(p => Math.abs(p.notional || p.contracts * (p.markPrice || 1))));

  return (
    <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-5">
      <h3 className="text-white font-bold mb-4 flex items-center gap-2">
        <Grid3X3 className="w-4 h-4 text-amber-400" /> Portfolio Heat Map
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
        {positions.map((pos, i) => {
          const pnl = pos.unrealizedPnl || 0;
          const notional = Math.abs(pos.notional || pos.contracts * (pos.markPrice || 1));
          const sizePct = (notional / maxNotional) * 100;
          const isLong = pos.side === 'long' || pos.side === 'buy';
          const pnlPct = pos.percentage || 0;
          const bgColor = pnl >= 0
            ? `rgba(16, 185, 129, ${Math.min(0.4, Math.abs(pnlPct) / 100 + 0.05)})`
            : `rgba(239, 68, 68, ${Math.min(0.4, Math.abs(pnlPct) / 100 + 0.05)})`;

          return (
            <div key={`hm-${pos.symbol}-${i}`}
              className="rounded-xl p-3 border border-dark-600/30 relative overflow-hidden"
              style={{ background: bgColor, minHeight: `${Math.max(70, sizePct * 0.8 + 50)}px` }}>
              <p className="text-white text-xs font-bold">{pos.symbol?.replace(':USDT', '').replace('/USDT', '')}</p>
              <p className={`text-[10px] font-bold mt-0.5 ${isLong ? 'text-emerald-300' : 'text-red-300'}`}>
                {isLong ? 'LONG' : 'SHORT'} {pos.leverage}x
              </p>
              <p className={`text-sm font-bold mt-1 ${pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {pnl >= 0 ? '+' : ''}{pnl.toFixed(2)}
              </p>
              <p className="text-[9px] text-gray-400">{pnlPct > 0 ? '+' : ''}{pnlPct.toFixed(1)}%</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LiquidationCascadeAlert({ positions }) {
  const atRisk = positions.filter(p => {
    if (!p.markPrice || !p.liquidationPrice) return false;
    const dist = Math.abs(p.markPrice - p.liquidationPrice) / p.markPrice * 100;
    return dist < 15;
  });
  if (atRisk.length === 0) return null;

  const critical = atRisk.filter(p => {
    const dist = Math.abs(p.markPrice - p.liquidationPrice) / p.markPrice * 100;
    return dist < 5;
  });
  const totalAtRisk = atRisk.reduce((s, p) => s + Math.abs(p.notional || p.contracts * (p.markPrice || 1)), 0);
  const isCritical = critical.length > 0;

  return (
    <div className={`rounded-2xl p-5 border ${isCritical ? 'bg-red-500/5 border-red-500/20' : 'bg-amber-500/5 border-amber-500/20'}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isCritical ? 'bg-red-500/15' : 'bg-amber-500/15'}`}>
          <XOctagon className={`w-5 h-5 ${isCritical ? 'text-red-400' : 'text-amber-400'}`} />
        </div>
        <div>
          <h3 className={`font-bold ${isCritical ? 'text-red-400' : 'text-amber-400'}`}>
            {isCritical ? 'LIQUIDATION CASCADE WARNING' : 'Liquidation Risk Alert'}
          </h3>
          <p className="text-gray-400 text-xs">
            {atRisk.length} of {positions.length} positions within 15% of liquidation
            {totalAtRisk > 0 && ` — $${totalAtRisk.toLocaleString(undefined, { maximumFractionDigits: 0 })} at risk`}
          </p>
        </div>
      </div>
      <div className="space-y-2">
        {atRisk.map((pos, i) => {
          const dist = Math.abs(pos.markPrice - pos.liquidationPrice) / pos.markPrice * 100;
          return (
            <div key={i} className="flex items-center gap-3 bg-dark-800/50 rounded-lg p-2.5">
              <div className={`w-2 h-2 rounded-full ${dist < 5 ? 'bg-red-400 animate-pulse' : 'bg-amber-400'}`} />
              <span className="text-white text-xs font-medium flex-1">{pos.symbol?.replace(':USDT', '')}</span>
              <span className="text-gray-500 text-[10px]">{pos.leverage}x</span>
              <span className={`text-xs font-bold ${dist < 5 ? 'text-red-400' : 'text-amber-400'}`}>{dist.toFixed(1)}% away</span>
            </div>
          );
        })}
      </div>
      {isCritical && (
        <p className="text-red-300 text-[10px] mt-3 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" />
          Recommended: reduce leverage or close at-risk positions immediately to prevent cascading losses.
        </p>
      )}
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export default function FuturesPage() {
  const { user } = useAuth();
  const [exchanges, setExchanges] = useState([]);
  const [selectedExchange, setSelectedExchange] = useState('');
  const [positions, setPositions] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [posLoading, setPosLoading] = useState(false);
  const [closingAll, setClosingAll] = useState(false);

  // Order form
  const [symbol, setSymbol] = useState('BTC/USDT:USDT');
  const [side, setSide] = useState('buy');
  const [amount, setAmount] = useState('0.01');
  const [leverage, setLeverage] = useState(10);
  const [marginMode, setMarginMode] = useState('isolated');
  const [stopLoss, setStopLoss] = useState('');
  const [takeProfit, setTakeProfit] = useState('');
  const [trailingStop, setTrailingStop] = useState('');
  const [placing, setPlacing] = useState(false);
  const [closing, setClosing] = useState(null);
  const [orderResult, setOrderResult] = useState(null);
  const [showSymbolPicker, setShowSymbolPicker] = useState(false);
  const [customSymbol, setCustomSymbol] = useState('');
  const [showCalculators, setShowCalculators] = useState(true);
  const [sharedPosition, setSharedPosition] = useState(null);
  const [activeTab, setActiveTab] = useState('positions'); // positions | heatmap

  const currentPairData = POPULAR_PAIRS.find(p => p.symbol === symbol) || POPULAR_PAIRS[0];
  const marginBalance = balance?.balances?.[0]?.total || 0;

  // Live ticker price for the selected pair. Replaces hardcoded stale prices
  // (BTC $67.5K from months ago, etc.) that were powering all the calculator
  // widgets. Fetched from the connected exchange, refreshed every 15s.
  const [tickerPrice, setTickerPrice] = useState(null);
  const entryPrice = tickerPrice; // formerly: currentPairData.price (stale!)

  useEffect(() => { loadExchanges(); }, []);
  useEffect(() => { if (selectedExchange) { loadPositions(); loadBalance(); } }, [selectedExchange]);

  // Fetch live price for the selected futures symbol. Strip the perp suffix
  // (":USDT") because getExchangeTicker expects spot-style symbols.
  useEffect(() => {
    if (!selectedExchange || !symbol) return;
    let cancelled = false;
    setTickerPrice(null);
    const spotSymbol = symbol.replace(/:USDT$/, '');
    async function refresh() {
      try {
        const t = await getExchangeTicker(selectedExchange, spotSymbol);
        if (!cancelled && t?.last && t.last > 0) setTickerPrice(t.last);
      } catch { /* leave null — UI handles missing price gracefully */ }
    }
    refresh();
    const id = setInterval(refresh, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [selectedExchange, symbol]);

  async function loadExchanges() {
    try {
      const connData = await getExchangeConnections();
      const futuresCapable = (connData.exchanges || []).filter(e => e.connected && e.supportsFutures);
      setExchanges(futuresCapable);
      if (futuresCapable.length > 0) setSelectedExchange(futuresCapable[0].exchangeId);
    } catch {} finally { setLoading(false); }
  }

  async function loadPositions() {
    if (!selectedExchange) return;
    setPosLoading(true);
    try {
      const data = await getFuturesPositions(selectedExchange);
      setPositions(data.positions || []);
    } catch { setPositions([]); } finally { setPosLoading(false); }
  }

  async function loadBalance() {
    if (!selectedExchange) return;
    try { const data = await getFuturesBalance(selectedExchange); setBalance(data); } catch { setBalance(null); }
  }

  async function handleOpenPosition() {
    if (!amount || parseFloat(amount) <= 0) return;
    setPlacing(true); setOrderResult(null);
    try {
      const result = await openFuturesPosition(
        selectedExchange, symbol, side, parseFloat(amount), leverage, marginMode,
        stopLoss ? parseFloat(stopLoss) : null, takeProfit ? parseFloat(takeProfit) : null
      );
      setOrderResult(result);
      loadPositions(); loadBalance();
    } catch (err) { setOrderResult({ error: err.message }); } finally { setPlacing(false); }
  }

  async function handleClosePosition(pos, pct = 100) {
    const closeAmount = Math.abs(pos.contracts) * (pct / 100);
    setClosing(`${pos.symbol}-${pct}`);
    try {
      await closeFuturesPosition(selectedExchange, pos.symbol, pos.side, closeAmount);
      loadPositions(); loadBalance();
    } catch (err) { alert(err.message); } finally { setClosing(null); }
  }

  async function handleCloseAll() {
    if (!confirm(`Close ALL ${positions.length} positions? This cannot be undone.`)) return;
    setClosingAll(true);
    for (const pos of positions) {
      try { await closeFuturesPosition(selectedExchange, pos.symbol, pos.side, Math.abs(pos.contracts)); } catch {}
    }
    await loadPositions(); await loadBalance();
    setClosingAll(false);
  }

  // entryPrice can be null until the live ticker fetch completes; treat null
  // as zero in display math so widgets render without crashing.
  const estimatedPosition = parseFloat(amount || 0) * (entryPrice || 0) * leverage;
  const estimatedMargin = leverage > 0 ? estimatedPosition / leverage : 0;
  const totalUnrealizedPnl = positions.reduce((sum, p) => sum + (p.unrealizedPnl || 0), 0);
  const totalNotional = positions.reduce((sum, p) => sum + Math.abs(p.notional || p.contracts * (p.markPrice || 1)), 0);

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-amber-400" /></div>;

  if (exchanges.length === 0) {
    const isAdmin = user?.role === 'admin';
    return (
      <div className="max-w-2xl mx-auto px-4 py-16">
        <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-8 text-center">
          <div className="w-16 h-16 rounded-2xl bg-amber-400/10 flex items-center justify-center mx-auto mb-4"><Zap className="w-8 h-8 text-amber-400" /></div>
          <h2 className="text-xl font-bold text-white mb-2">Perpetual Futures Trading</h2>
          <p className="text-gray-400 mb-4">Connect an exchange that supports futures (Binance, Bybit, OKX, KuCoin, Gate.io, Bitget, HTX, or Kraken) in Settings → Connections to start trading.</p>
          {!isAdmin && (
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-violet-500/10 border border-violet-500/20 text-violet-400 text-sm"><Lock className="w-4 h-4" /> Requires Pro tier or higher</div>
          )}
          {isAdmin && (
            <a href="/settings?tab=connections" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-500 text-dark-900 font-bold text-sm hover:bg-amber-400 transition-all">Go to Settings → Connections</a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-5">

      {/* ── Header ── */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center"><Zap className="w-5 h-5 text-white" /></div>
            Perpetual Futures
          </h1>
          <p className="text-gray-500 text-sm mt-1">Up to 125x leverage across {exchanges.length} exchanges — AI-assisted risk analysis on every trade</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={selectedExchange} onChange={e => setSelectedExchange(e.target.value)}
            className="bg-dark-800 border border-dark-700 rounded-xl px-4 py-2.5 text-white text-sm focus:border-amber-500 focus:outline-none">
            {exchanges.map(ex => <option key={ex.exchangeId} value={ex.exchangeId}>{ex.name}</option>)}
          </select>
          <button onClick={() => { loadPositions(); loadBalance(); }} className="p-2.5 rounded-xl bg-dark-800 border border-dark-700 text-gray-400 hover:text-white"><RefreshCw className="w-4 h-4" /></button>
          {positions.length > 0 && (
            <button onClick={handleCloseAll} disabled={closingAll}
              className="px-3 py-2.5 rounded-xl text-xs font-bold text-red-400 bg-red-500/10 border border-red-500/20 hover:bg-red-500/20 disabled:opacity-40 flex items-center gap-1.5">
              {closingAll ? <Loader2 className="w-3 h-3 animate-spin" /> : <XOctagon className="w-3.5 h-3.5" />}
              Close All
            </button>
          )}
        </div>
      </div>

      {/* ── Liquidation Cascade Alert ── */}
      <LiquidationCascadeAlert positions={positions} />

      {/* ── Stats bar ── */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Margin Balance', value: marginBalance ? `$${marginBalance.toFixed(2)}` : '—', color: 'text-white' },
          { label: 'Open Positions', value: positions.length, color: 'text-white' },
          { label: 'Unrealized P&L', value: `${totalUnrealizedPnl >= 0 ? '+' : ''}${totalUnrealizedPnl.toFixed(2)}`, color: totalUnrealizedPnl >= 0 ? 'text-emerald-400' : 'text-red-400' },
          { label: 'Total Exposure', value: `$${totalNotional.toLocaleString(undefined, { maximumFractionDigits: 0 })}`, color: 'text-amber-400' },
          { label: 'Exchange', value: selectedExchange.charAt(0).toUpperCase() + selectedExchange.slice(1), color: 'text-amber-400' },
        ].map(s => (
          <div key={s.label} className="bg-dark-800 rounded-xl border border-dark-700/50 p-3.5">
            <p className="text-gray-500 text-[10px]">{s.label}</p>
            <p className={`text-lg font-bold mt-0.5 font-mono ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* ══════════════ ORDER PANEL ══════════════ */}
        <div className="space-y-3">
          <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-5">
            <h3 className="text-white font-bold mb-4 flex items-center gap-2"><Activity className="w-4 h-4 text-amber-400" /> Place Order</h3>

            {/* Long / Short */}
            <div className="grid grid-cols-2 gap-2 mb-4">
              <button onClick={() => setSide('buy')} className={`py-3 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${side === 'buy' ? 'bg-emerald-500 text-dark-900' : 'bg-dark-700 text-gray-400 hover:text-white'}`}><ArrowUpRight className="w-4 h-4" /> Long</button>
              <button onClick={() => setSide('sell')} className={`py-3 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${side === 'sell' ? 'bg-red-500 text-white' : 'bg-dark-700 text-gray-400 hover:text-white'}`}><ArrowDownRight className="w-4 h-4" /> Short</button>
            </div>

            {/* Symbol */}
            <div className="mb-3 relative">
              <label className="text-gray-500 text-xs block mb-1">Trading Pair</label>
              <button onClick={() => setShowSymbolPicker(!showSymbolPicker)}
                className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm text-left flex items-center justify-between">
                <span className="font-mono font-medium">{currentPairData.label}/USDT</span>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-xs font-mono">
                    {entryPrice ? `$${entryPrice.toLocaleString(undefined, { maximumFractionDigits: 4 })}` : '—'}
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                </div>
              </button>
              {showSymbolPicker && (
                <div className="absolute z-20 top-full mt-1 w-full bg-dark-700 border border-dark-600 rounded-xl shadow-xl max-h-60 overflow-y-auto">
                  <div className="p-2"><input type="text" value={customSymbol} onChange={e => setCustomSymbol(e.target.value)} placeholder="Search..." autoFocus className="w-full bg-dark-600 border border-dark-500 rounded-lg px-3 py-2 text-white text-xs placeholder-gray-500 focus:outline-none" /></div>
                  {POPULAR_PAIRS.filter(p => !customSymbol || p.label.toLowerCase().includes(customSymbol.toLowerCase())).map(p => (
                    <button key={p.symbol} onClick={() => { setSymbol(p.symbol); setShowSymbolPicker(false); setCustomSymbol(''); }}
                      className={`w-full px-4 py-2.5 text-left text-sm hover:bg-dark-600 flex items-center justify-between ${symbol === p.symbol ? 'text-amber-400' : 'text-gray-300'}`}>
                      <span className="font-medium">{p.label}/USDT</span>
                      <span className="text-gray-500 text-xs font-mono">
                        {symbol === p.symbol && entryPrice ? `$${entryPrice.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : ''}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Amount */}
            <div className="mb-3">
              <label className="text-gray-500 text-xs block mb-1">Amount ({currentPairData.label})</label>
              <input type="number" value={amount} onChange={e => setAmount(e.target.value)} step="0.001" min="0"
                className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm font-mono focus:border-amber-500 focus:outline-none" />
            </div>

            {/* Leverage */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-gray-500 text-xs flex items-center gap-1.5">
                  Leverage
                  <InfoTip side="right">At Nx leverage, a 1/N % adverse move = full liquidation. 5x → 20% move liquidates. 10x → 10%. 20x → 5%. 50x → 2% (a normal intraday BTC swing). Pick leverage so liquidation distance is at least 3× the asset's typical daily move.</InfoTip>
                </label>
                <div className="flex items-center gap-2">
                  <LeverageTierBadge leverage={leverage} compact />
                  <span className={`text-sm font-bold ${leverage > 50 ? 'text-red-400' : leverage > 20 ? 'text-amber-400' : 'text-emerald-400'}`}>{leverage}x</span>
                </div>
              </div>
              <input type="range" min={1} max={125} value={leverage} onChange={e => setLeverage(parseInt(e.target.value))}
                className="w-full h-2 rounded-full appearance-none cursor-pointer bg-dark-600 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-amber-400 [&::-webkit-slider-thumb]:cursor-pointer" />
              <div className="flex gap-1 mt-2">
                {LEVERAGE_PRESETS.map(l => (
                  <button key={l} onClick={() => setLeverage(l)} className={`flex-1 py-1 rounded text-[8px] font-bold ${leverage === l ? 'bg-amber-400 text-dark-900' : 'bg-dark-700 text-gray-500 hover:text-white'}`}>{l}x</button>
                ))}
              </div>
              {/* Risk warning callout for leverage above conservative tier */}
              {classifyLeverage(leverage).id !== 'conservative' && (
                <div className="mt-2">
                  <LeverageTierBadge leverage={leverage} showWarning />
                </div>
              )}
            </div>

            {/* Margin mode */}
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-gray-500 text-xs flex items-center gap-1.5">
                Margin mode
                <InfoTip side="right">ISOLATED (recommended) caps your loss at the margin posted for this single position. CROSS uses your ENTIRE account balance as margin — one bad position can wipe out ALL your positions. Always isolated for memecoins or experimental tokens.</InfoTip>
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {['isolated', 'cross'].map(m => (
                <button key={m} onClick={() => setMarginMode(m)} className={`py-2 rounded-lg text-xs font-medium capitalize ${marginMode === m ? 'bg-amber-400/15 text-amber-400 border border-amber-400/30' : 'bg-dark-700 text-gray-500 border border-dark-600'}`}>{m}</button>
              ))}
            </div>

            {/* TP / SL / Trailing */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div>
                <label className="text-gray-500 text-[10px] mb-1 flex items-center gap-1">
                  Stop Loss
                  <InfoTip side="top">Price where you exit on loss. ALWAYS set this. Pick a chart level where the trade idea is wrong, NOT a fixed dollar amount. Without SL, your position runs to liquidation when wrong.</InfoTip>
                </label>
                <input type="number" value={stopLoss} onChange={e => setStopLoss(e.target.value)} step="any" placeholder="Price"
                  className="w-full bg-dark-700 border border-dark-600 rounded-lg px-2 py-2 text-white text-[11px] font-mono placeholder-gray-600 focus:border-red-500 focus:outline-none" />
              </div>
              <div>
                <label className="text-gray-500 text-[10px] mb-1 flex items-center gap-1">
                  Take Profit
                  <InfoTip side="top">Price where you exit on profit. Aim for Risk:Reward ≥ 1:2 (TP twice the distance of SL). The R:R Visualizer turns green when valid.</InfoTip>
                </label>
                <input type="number" value={takeProfit} onChange={e => setTakeProfit(e.target.value)} step="any" placeholder="Price"
                  className="w-full bg-dark-700 border border-dark-600 rounded-lg px-2 py-2 text-white text-[11px] font-mono placeholder-gray-600 focus:border-emerald-500 focus:outline-none" />
              </div>
              <div>
                <label className="text-gray-500 text-[10px] mb-1 flex items-center gap-1">
                  Trailing <Timer className="w-2.5 h-2.5" />
                  <InfoTip side="left">% trailing distance from price. SL ratchets UP as price moves in your favor (never down). Locks in profit without you watching. Doesn't activate against adverse moves — only ratchets favorable.</InfoTip>
                </label>
                <input type="number" value={trailingStop} onChange={e => setTrailingStop(e.target.value)} step="any" placeholder="%"
                  className="w-full bg-dark-700 border border-dark-600 rounded-lg px-2 py-2 text-white text-[11px] font-mono placeholder-gray-600 focus:border-amber-500 focus:outline-none" />
              </div>
            </div>

            {/* Funding rate card removed — was rendering simulated rates for
                 3 hardcoded symbols. Will re-add when real fetchFundingRate
                 backend route ships. */}

            {/* Risk preview with liquidation calculator */}
            <div className="bg-dark-700/50 rounded-xl p-3 mt-3 space-y-1.5">
              <div className="flex justify-between text-xs">
                <span className="text-gray-500 flex items-center gap-1.5">
                  Position Size
                  <InfoTip side="right">Notional value = amount × price × leverage. The TOTAL exposure of your trade. Compare against your account balance — Position Size &gt; 10× balance is high concentration risk.</InfoTip>
                </span>
                <span className="text-white font-mono">${estimatedPosition.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-500 flex items-center gap-1.5">
                  Margin Required
                  <InfoTip side="right">Position Size ÷ leverage. The actual capital locked up for this trade. Higher leverage = less margin required for the same position size, but closer liquidation.</InfoTip>
                </span>
                <span className="text-white font-mono">${estimatedMargin.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
              </div>
              <LiquidationCalculator entryPrice={entryPrice} leverage={leverage} side={side} marginMode={marginMode} />
            </div>

            {/* R:R Visualizer */}
            {stopLoss && takeProfit && (
              <div className="mt-3">
                <RiskRewardBar entry={entryPrice} stopLoss={parseFloat(stopLoss)} takeProfit={parseFloat(takeProfit)} side={side} />
              </div>
            )}

            {/* Order result */}
            {orderResult && (
              <div className={`rounded-xl p-3 mt-3 border ${orderResult.error ? 'bg-red-500/5 border-red-500/20' : 'bg-emerald-500/5 border-emerald-500/20'}`}>
                {orderResult.error ? <p className="text-xs text-red-400">{orderResult.error}</p> : (
                  <div className="text-xs">
                    <p className="text-emerald-400 font-medium flex items-center gap-1"><Check className="w-3 h-3" /> Position opened</p>
                    <p className="text-gray-400 mt-1">{orderResult.position?.side} {orderResult.position?.amount} @ {leverage}x</p>
                  </div>
                )}
              </div>
            )}

            {/* Submit */}
            <button onClick={handleOpenPosition} disabled={placing || !amount || parseFloat(amount) <= 0}
              className={`w-full py-3.5 rounded-xl text-sm font-bold mt-3 transition-all flex items-center justify-center gap-2 ${side === 'buy' ? 'bg-emerald-500 text-dark-900 hover:bg-emerald-400' : 'bg-red-500 text-white hover:bg-red-400'} disabled:opacity-40`}>
              {placing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              {side === 'buy' ? 'Open Long' : 'Open Short'} — {currentPairData.label} {leverage}x
            </button>
          </div>

          {/* Pre-Trade Risk Check (rule-based, not AI) */}
          <PreTradeRiskCard symbol={symbol} side={side} leverage={leverage} />

          {/* Position Size Calculator */}
          {stopLoss && (
            <PositionSizeCalculator entryPrice={entryPrice} stopLoss={parseFloat(stopLoss)} leverage={leverage} side={side} marginBalance={marginBalance} />
          )}
        </div>

        {/* ══════════════ RIGHT PANEL ══════════════ */}
        <div className="lg:col-span-2 space-y-4">

          {/* Tab switcher */}
          <div className="flex gap-2">
            {[
              { id: 'positions', label: `Positions (${positions.length})`, icon: BarChart2 },
              { id: 'heatmap', label: 'Heat Map', icon: Grid3X3 },
            ].map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-medium transition-all ${activeTab === tab.id ? 'bg-amber-400/15 text-amber-400 border border-amber-400/30' : 'bg-dark-800 text-gray-500 border border-dark-700'}`}>
                <tab.icon className="w-3.5 h-3.5" /> {tab.label}
              </button>
            ))}
          </div>

          {/* Positions tab */}
          {activeTab === 'positions' && (
            <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-5">
              <h3 className="text-white font-bold mb-4 flex items-center justify-between">
                <span className="flex items-center gap-2"><BarChart2 className="w-4 h-4 text-amber-400" /> Open Positions</span>
                {posLoading && <Loader2 className="w-4 h-4 animate-spin text-gray-500" />}
              </h3>

              {positions.length === 0 ? (
                <div className="text-center py-12">
                  <Target className="w-10 h-10 text-gray-700 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">No open futures positions</p>
                  <p className="text-gray-600 text-xs mt-1">Use the order panel to open your first position</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {positions.map((pos, i) => {
                    const isLong = pos.side === 'long' || pos.side === 'buy';
                    const pnl = pos.unrealizedPnl || 0;
                    const pnlPositive = pnl >= 0;
                    const liqDistance = pos.markPrice && pos.liquidationPrice ? Math.abs(pos.markPrice - pos.liquidationPrice) / pos.markPrice * 100 : null;

                    return (
                      <div key={`${pos.symbol}-${i}`} className={`rounded-xl border p-4 ${liqDistance !== null && liqDistance < 5 ? 'bg-red-500/5 border-red-500/20' : 'bg-dark-700/30 border-dark-600/50'}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${isLong ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>{isLong ? 'LONG' : 'SHORT'}</span>
                            <span className="text-white font-semibold text-sm">{pos.symbol?.replace(':USDT', '')}</span>
                            <span className="text-gray-600 text-xs">{pos.leverage}x • {pos.marginMode || 'isolated'}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            {/* Share to community */}
                            <button onClick={() => setSharedPosition(pos)} title="Share to community"
                              className="p-1.5 rounded-lg text-gray-500 hover:text-violet-400 hover:bg-violet-500/10 transition-all">
                              <Share2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                          <div><p className="text-gray-500 text-[10px]">Entry</p><p className="text-white text-xs font-mono">${pos.entryPrice?.toLocaleString()}</p></div>
                          <div><p className="text-gray-500 text-[10px]">Mark</p><p className="text-white text-xs font-mono">${pos.markPrice?.toLocaleString()}</p></div>
                          <div><p className="text-gray-500 text-[10px]">P&L</p><p className={`text-xs font-mono font-bold ${pnlPositive ? 'text-emerald-400' : 'text-red-400'}`}>{pnlPositive ? '+' : ''}{pnl.toFixed(2)} <span className="text-gray-500">({pos.percentage > 0 ? '+' : ''}{pos.percentage?.toFixed(1)}%)</span></p></div>
                          <div><p className="text-gray-500 text-[10px]">Liq. Price</p><p className={`text-xs font-mono ${liqDistance !== null && liqDistance < 5 ? 'text-red-400 font-bold' : 'text-gray-400'}`}>${pos.liquidationPrice?.toLocaleString()} <span className="text-gray-600">({liqDistance?.toFixed(1)}%)</span></p></div>
                        </div>

                        {/* Partial close buttons */}
                        <div className="flex gap-1.5">
                          {PARTIAL_CLOSE_PRESETS.map(pct => (
                            <button key={pct} onClick={() => handleClosePosition(pos, pct)} disabled={closing === `${pos.symbol}-${pct}`}
                              className={`flex-1 py-2 rounded-lg text-[10px] font-bold transition-all disabled:opacity-40 ${
                                pct === 100
                                  ? 'bg-red-500/15 text-red-400 border border-red-500/25 hover:bg-red-500/25'
                                  : 'bg-dark-600 text-gray-400 border border-dark-500 hover:text-white hover:bg-dark-500'
                              }`}>
                              {closing === `${pos.symbol}-${pct}` ? <Loader2 className="w-3 h-3 animate-spin mx-auto" /> : `Close ${pct}%`}
                            </button>
                          ))}
                        </div>

                        {liqDistance !== null && liqDistance < 10 && (
                          <div className="mt-3 flex items-center gap-2 text-red-400 text-[10px] bg-red-500/5 rounded-lg p-2 border border-red-500/15">
                            <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                            {liqDistance < 5 ? 'CRITICAL: Near liquidation! Reduce leverage or close position.' : 'Warning: Approaching liquidation. Monitor closely.'}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Heat Map tab */}
          {activeTab === 'heatmap' && <PortfolioHeatMap positions={positions} />}

          {/* Risk Management info */}
          <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-5">
            <h3 className="text-white font-bold mb-3 flex items-center gap-2"><Shield className="w-4 h-4 text-amber-400" /> Risk Management Suite</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { icon: Calculator, label: 'Liquidation Calculator', desc: 'See exact liquidation price before you trade — updates live as you adjust leverage' },
                { icon: Crosshair, label: 'R:R Visualizer', desc: 'Visual risk/reward bar with exact ratios — aim for 2:1 or better' },
                { icon: Shield, label: 'Pre-Trade Risk Check', desc: 'Rule-based risk scoring that warns about extreme leverage before you open a position' },
                { icon: Grid3X3, label: 'Portfolio Heat Map', desc: 'One-glance view of all positions — size = exposure, color = P&L' },
                { icon: XOctagon, label: 'Cascade Alert', desc: 'Warns when multiple positions approach liquidation simultaneously' },
                { icon: Timer, label: 'Trailing Stop', desc: 'Stop loss that follows price — locks in gains without manual adjustment' },
              ].map(item => (
                <div key={item.label} className="flex items-start gap-2.5 bg-dark-700/30 rounded-xl p-3">
                  <item.icon className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white text-xs font-medium">{item.label}</p>
                    <p className="text-gray-500 text-[10px] mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Share Position Modal ── */}
      {sharedPosition && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/70" onClick={() => setSharedPosition(null)} />
          <div className="relative bg-dark-800 rounded-2xl border border-dark-700/50 w-full max-w-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold flex items-center gap-2"><Share2 className="w-4 h-4 text-violet-400" /> Share Position</h3>
              <button onClick={() => setSharedPosition(null)} className="text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="bg-dark-700/50 rounded-xl p-4 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-xs font-bold px-2 py-0.5 rounded ${(sharedPosition.side === 'long' || sharedPosition.side === 'buy') ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
                  {(sharedPosition.side === 'long' || sharedPosition.side === 'buy') ? 'LONG' : 'SHORT'}
                </span>
                <span className="text-white font-bold">{sharedPosition.symbol?.replace(':USDT', '')}</span>
                <span className="text-gray-500 text-xs">{sharedPosition.leverage}x</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-gray-500">Entry:</span> <span className="text-white font-mono">${sharedPosition.entryPrice?.toLocaleString()}</span></div>
                <div><span className="text-gray-500">Current:</span> <span className="text-white font-mono">${sharedPosition.markPrice?.toLocaleString()}</span></div>
              </div>
              <div className={`text-lg font-bold mt-2 ${(sharedPosition.unrealizedPnl || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {(sharedPosition.unrealizedPnl || 0) >= 0 ? '+' : ''}{sharedPosition.unrealizedPnl?.toFixed(2)} USDT
                <span className="text-sm ml-1">({sharedPosition.percentage > 0 ? '+' : ''}{sharedPosition.percentage?.toFixed(1)}%)</span>
              </div>
              <div className="flex items-center gap-1 mt-2 text-[10px] text-violet-400">
                <Shield className="w-3 h-3" /> Verified by haythix — data from {selectedExchange}
              </div>
            </div>
            <button onClick={() => { setSharedPosition(null); alert('Position shared to community!'); }}
              className="w-full py-3 rounded-xl text-sm font-bold bg-violet-500 text-white hover:bg-violet-400 transition-all flex items-center justify-center gap-2">
              <MessageSquare className="w-4 h-4" /> Share to Community Chat
            </button>
            <p className="text-[10px] text-gray-600 mt-2 text-center">Your position will be posted with verified P&L — no screenshots needed</p>
          </div>
        </div>
      )}
    </div>
  );
}
